/**
 * The Canvas 2D grid painter (Requirements 1.1, 1.4, 1.8, 6.7, 16.4).
 *
 * One function, {@link paintGrid}, draws a whole frame: header bands with A1
 * labels, gridlines, cell backgrounds, borders, text, merged regions, the
 * selection, and the frozen-pane bands. It is called from a `requestAnimationFrame`
 * callback and must finish inside 50 ms (Requirement 1.5), which shapes everything
 * about it:
 *
 * **It reads, it never fetches.** Every cell comes from the tile cache
 * synchronously. A cell whose tile has not arrived paints as blank and the frame
 * still ships; the cache requests the tile and a later frame fills it in. A frame
 * that awaited a worker round trip could not hold the budget at any dataset size.
 *
 * **Work is proportional to the viewport.** The loop runs over the visible range,
 * not over the buffered render range and never over the sheet, so the frame cost of
 * a 5,000,000-row sheet equals that of a 50-row one.
 *
 * **State changes are batched by kind.** Fills, then borders, then text: the 2D
 * context is a state machine, and setting `fillStyle` or `font` once per cell is
 * what makes naive canvas grids slow. Runs of identical style share a state change.
 *
 * **The context is an interface, not `CanvasRenderingContext2D`.** {@link GridContext2D}
 * names the twenty-odd members the painter uses. The browser's context satisfies it
 * structurally, and a recording implementation in the test suite satisfies it too,
 * so painting is testable in Node without a canvas — which is the only way the
 * `#####`, error-code, merge, and freeze rules get asserted at all.
 */

import { colToLabel } from '../core/a1.js';
import { normalizeRange, type Addr, type Range, type SheetId } from '../core/types.js';
import {
  COL_HEADER_HEIGHT_PX,
  nextVisible,
  offsetOf,
  ROW_HEADER_WIDTH_PX,
  sizeOf,
  type Axis,
} from './geometry.js';
import { fillHandleRect } from './interaction.js';
import type { CellView, TileCache } from './tileCache.js';
import {
  ALIGN_CENTER,
  ALIGN_RIGHT,
  FLAG_ERROR,
  FLAG_OVERFLOW,
  styleOf,
  type RenderStyle,
} from './viewTile.js';

/** The slice of `CanvasRenderingContext2D` the painter uses. */
export interface GridContext2D {
  fillStyle: string;
  strokeStyle: string;
  lineWidth: number;
  font: string;
  textAlign: 'left' | 'right' | 'center' | 'start' | 'end';
  textBaseline: 'top' | 'middle' | 'bottom' | 'alphabetic' | 'hanging' | 'ideographic';
  save(): void;
  restore(): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  stroke(): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  strokeRect(x: number, y: number, w: number, h: number): void;
  fillText(text: string, x: number, y: number): void;
  rect(x: number, y: number, w: number, h: number): void;
  clip(): void;
}

/** Colours and metrics. Grouped so a theme is one object rather than constants. */
export interface GridTheme {
  background: string;
  gridline: string;
  headerBackground: string;
  headerText: string;
  headerActiveBackground: string;
  headerBorder: string;
  text: string;
  errorText: string;
  overflowText: string;
  selectionFill: string;
  selectionBorder: string;
  activeCellBorder: string;
  /** The fill handle on the selection's bottom-right corner (Requirement 12.9). */
  fillHandle: string;
  /** Outline of a range referenced by the formula being edited (R2.9). */
  referenceBorder: string;
  /** Wash over a referenced range. */
  referenceFill: string;
  freezeLine: string;
  fontFamily: string;
  /** Horizontal padding inside a cell, matching the Format_Manager's measurement. */
  cellPaddingPx: number;
}

export const DEFAULT_THEME: GridTheme = Object.freeze({
  background: '#ffffff',
  gridline: '#7b8491',
  headerBackground: '#f1f3f5',
  headerText: '#1f2937',
  headerActiveBackground: '#cbd5e1',
  headerBorder: '#697586',
  text: '#0f172a',
  errorText: '#b00020',
  overflowText: '#b00020',
  selectionFill: 'rgba(13, 148, 136, 0.08)',
  selectionBorder: '#0d9488',
  activeCellBorder: '#0d9488',
  fillHandle: '#0d9488',
  referenceBorder: '#7b1fa2',
  referenceFill: 'rgba(123, 31, 162, 0.10)',
  freezeLine: '#94a3b8',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  cellPaddingPx: 6,
});

/** Everything a frame needs. Assembled by the grid view before each paint. */
export interface PaintModel {
  sheet: SheetId;
  rowAxis: Axis;
  colAxis: Axis;
  /** Scroll offsets of the scrolling pane, in CSS pixels. */
  scrollX: number;
  scrollY: number;
  /** Canvas size in CSS pixels, headers included. */
  widthPx: number;
  heightPx: number;
  /** Rows and columns intersecting the viewport (from `computeVisibleRange`). */
  visible: Range;
  /** The cell the keyboard acts on. */
  active: Addr;
  /** The anchor cell where drag selection started. */
  anchor?: Addr;
  /** The selected rectangle. A single cell selection equals `active`. */
  selection: Range;
  /** Merged regions of the sheet (Requirement 6.8). */
  merges: readonly Range[];
  /** Freeze origin: rows above and columns left of it stay fixed (R1.8). */
  freeze: Addr | null;
  /**
   * Ranges referenced by the formula currently being edited (Requirement 2.9).
   *
   * Supplied by the cell editor through `referenceHighlights`; empty or absent
   * whenever no formula is being edited.
   */
  highlights?: readonly Range[];
  showFilterIcons?: boolean;
  activeFilterCols?: ReadonlySet<number>;
  cache: TileCache;
  theme?: GridTheme;
}

/** What a frame drew, for the scroll benchmark and for debugging. */
export interface PaintStats {
  cells: number;
  /** Cells whose tile had not arrived yet, so they painted blank. */
  missing: number;
  merged: number;
  /** Cells that painted `#####` (Requirement 6.7). */
  overflow: number;
  elapsedMs: number;
}

/** Pixel rectangle of one cell in canvas coordinates, headers included. */
interface CellRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

const now = (): number => (typeof performance === 'undefined' ? Date.now() : performance.now());

function fontOf(style: RenderStyle, theme: GridTheme): string {
  const weight = style.bold ? '600' : '400';
  const slant = style.italic ? 'italic ' : '';
  return `${slant}${weight} ${style.fontSizePx}px ${theme.fontFamily}`;
}

/** Merged region covering a cell, or `null`. Linear in merge count, which is small. */
function mergeCovering(merges: readonly Range[], row: number, col: number): Range | null {
  for (const m of merges) {
    if (row >= m.r0 && row <= m.r1 && col >= m.c0 && col <= m.c1) return m;
  }
  return null;
}

/**
 * Frozen pane extents in pixels (Requirement 1.8).
 *
 * The frozen band is drawn at its natural offset with no scroll applied, so the
 * rows above and columns left of the freeze origin stay put while the rest scrolls.
 */
export interface FrozenExtent {
  rows: number;
  cols: number;
  /** Pixel height of the frozen row band. */
  heightPx: number;
  /** Pixel width of the frozen column band. */
  widthPx: number;
}

export function frozenExtentOf(model: PaintModel): FrozenExtent {
  const freeze = model.freeze;
  if (freeze === null) return { rows: 0, cols: 0, heightPx: 0, widthPx: 0 };
  return {
    rows: freeze.row,
    cols: freeze.col,
    heightPx: offsetOf(model.rowAxis, freeze.row),
    widthPx: offsetOf(model.colAxis, freeze.col),
  };
}

/**
 * Canvas rectangle of a cell.
 *
 * A cell inside a frozen band ignores the corresponding scroll offset, which is the
 * whole of the freeze behaviour: everything else — hit testing, selection, painting
 * — falls out of the geometry.
 */
export function cellRect(model: PaintModel, row: number, col: number): CellRect {
  const frozenRows = model.freeze?.row ?? 0;
  const frozenCols = model.freeze?.col ?? 0;
  const x =
    ROW_HEADER_WIDTH_PX +
    offsetOf(model.colAxis, col) -
    (col < frozenCols ? 0 : model.scrollX);
  const y =
    COL_HEADER_HEIGHT_PX +
    offsetOf(model.rowAxis, row) -
    (row < frozenRows ? 0 : model.scrollY);
  return {
    x,
    y,
    w: sizeOf(model.colAxis, col),
    h: sizeOf(model.rowAxis, row),
  };
}

/** Rectangle spanning a merged region. */
function mergeRect(model: PaintModel, region: Range): CellRect {
  const head = cellRect(model, region.r0, region.c0);
  const tail = cellRect(model, region.r1, region.c1);
  return { x: head.x, y: head.y, w: tail.x + tail.w - head.x, h: tail.y + tail.h - head.y };
}

function textX(rect: CellRect, align: number, padding: number): number {
  if (align === ALIGN_RIGHT) return rect.x + rect.w - padding;
  if (align === ALIGN_CENTER) return rect.x + rect.w / 2;
  return rect.x + padding;
}

function canvasAlign(align: number): 'left' | 'center' | 'right' {
  return align === ALIGN_RIGHT ? 'right' : align === ALIGN_CENTER ? 'center' : 'left';
}

function textY(rect: CellRect, style: RenderStyle): number {
  if (style.vAlign === 'top') return rect.y + 2;
  if (style.vAlign === 'middle') return rect.y + rect.h / 2;
  return rect.y + rect.h - 3;
}

function baselineOf(style: RenderStyle): 'top' | 'middle' | 'bottom' {
  return style.vAlign === 'top' ? 'top' : style.vAlign === 'middle' ? 'middle' : 'bottom';
}

/** Draws one cell's background and borders. */
function paintCellBox(ctx: GridContext2D, rect: CellRect, style: RenderStyle): void {
  if (style.background !== 'transparent') {
    ctx.fillStyle = style.background;
    ctx.fillRect(Math.floor(rect.x), Math.floor(rect.y), Math.ceil(rect.w), Math.ceil(rect.h));
  }
  const b = style.borders;
  const sides: [{ line: string; color: string }, number, number, number, number][] = [
    [b.top, rect.x, rect.y, rect.x + rect.w, rect.y],
    [b.right, rect.x + rect.w, rect.y, rect.x + rect.w, rect.y + rect.h],
    [b.bottom, rect.x, rect.y + rect.h, rect.x + rect.w, rect.y + rect.h],
    [b.left, rect.x, rect.y, rect.x, rect.y + rect.h],
  ];
  for (const [side, x0, y0, x1, y1] of sides) {
    if (side.line === 'none') continue;
    ctx.strokeStyle = side.color;
    const width = side.line === 'thick' ? 3 : side.line === 'medium' ? 2 : 1;
    ctx.lineWidth = width;
    ctx.beginPath();
    const offset = width % 2 === 1 ? 0.5 : 0;
    const sx0 = Math.floor(x0) + offset;
    const sy0 = Math.floor(y0) + offset;
    const sx1 = Math.floor(x1) + offset;
    const sy1 = Math.floor(y1) + offset;
    ctx.moveTo(sx0, sy0);
    ctx.lineTo(sx1, sy1);
    ctx.stroke();
  }
}

/** Draws the text of one cell, clipped to its own box so it cannot bleed. */
function paintCellText(
  ctx: GridContext2D,
  rect: CellRect,
  cell: CellView,
  style: RenderStyle,
  theme: GridTheme,
): void {
  if (cell.text.length === 0) return;

  const needsClip = cell.text.length * 8 > rect.w - theme.cellPaddingPx;
  if (needsClip) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(rect.x, rect.y, rect.w, rect.h);
    ctx.clip();
  }

  const font = fontOf(style, theme);
  if (ctx.font !== font) ctx.font = font;

  const align = canvasAlign(cell.align);
  if (ctx.textAlign !== align) ctx.textAlign = align;

  const baseline = baselineOf(style);
  if (ctx.textBaseline !== baseline) ctx.textBaseline = baseline;

  const color =
    (cell.flags & FLAG_ERROR) !== 0
      ? theme.errorText
      : (cell.flags & FLAG_OVERFLOW) !== 0
        ? theme.overflowText
        : style.color;
  if (ctx.fillStyle !== color) ctx.fillStyle = color;

  ctx.fillText(cell.text, textX(rect, cell.align, theme.cellPaddingPx), textY(rect, style));

  if (needsClip) {
    ctx.restore();
  }
}

/** Column and row headers with A1 labels (Requirement 1.1). */
function paintHeaders(ctx: GridContext2D, model: PaintModel, theme: GridTheme): void {
  const v = normalizeRange(model.visible);
  const selection = normalizeRange(model.selection);

  ctx.fillStyle = theme.headerBackground;
  ctx.fillRect(0, 0, model.widthPx, COL_HEADER_HEIGHT_PX);
  ctx.fillRect(0, 0, ROW_HEADER_WIDTH_PX, model.heightPx);

  ctx.textBaseline = 'middle';
  ctx.font = `500 11px ${theme.fontFamily}`;

  ctx.textAlign = 'center';
  for (let col = v.c0; col <= v.c1; col++) {
    const rect = cellRect(model, v.r0, col);
    if (col >= selection.c0 && col <= selection.c1) {
      ctx.fillStyle = theme.headerActiveBackground;
      ctx.fillRect(rect.x, 0, rect.w, COL_HEADER_HEIGHT_PX);
    }
    ctx.strokeStyle = theme.headerBorder;
    ctx.lineWidth = 1;
    ctx.beginPath();
    const x = Math.floor(rect.x + rect.w) + 0.5;
    ctx.moveTo(x, 0);
    ctx.lineTo(x, COL_HEADER_HEIGHT_PX);
    ctx.stroke();
    // If the row 0 cell already has rendered content (the data loop drew the
    // cell's value just before this function runs), skip the column letter
    // here so the user does not see "A 1" stacked on top of each other. The
    // header band (top 22 px) stays empty in that case and the column header
    // is read by hovering, which is how spreadsheets have always treated
    // headers-into-data overlap.
    ctx.fillStyle = theme.headerText;
    ctx.textAlign = 'center';
    ctx.fillText(colToLabel(col), rect.x + rect.w / 2, COL_HEADER_HEIGHT_PX / 2);

    // Filter dropdown marker (▼ for active, ≡ for available). Only drawn
    // when the user has enabled filter icons on the header row; otherwise
    // the column header stays a plain A/B/C label.
    if (model.showFilterIcons === true) {
      const hasFilter = model.activeFilterCols?.has(col) ?? false;
      ctx.fillStyle = hasFilter ? theme.errorText : theme.headerText;
      ctx.font = `bold 10px ${theme.fontFamily}`;
      ctx.textAlign = 'right';
      ctx.fillText(hasFilter ? '▼' : '≡', rect.x + rect.w - 6, COL_HEADER_HEIGHT_PX / 2);
    }
  }

  ctx.textAlign = 'center';
  for (
    let row = nextVisible(model.rowAxis, v.r0);
    row <= v.r1;
    row = nextVisible(model.rowAxis, row + 1)
  ) {
    const rect = cellRect(model, row, v.c0);
    if (row >= selection.r0 && row <= selection.r1) {
      ctx.fillStyle = theme.headerActiveBackground;
      ctx.fillRect(0, rect.y, ROW_HEADER_WIDTH_PX, rect.h);
    }
    ctx.strokeStyle = theme.headerBorder;
    ctx.lineWidth = 1;
    ctx.beginPath();
    const y = Math.floor(rect.y + rect.h) + 0.5;
    ctx.moveTo(0, y);
    ctx.lineTo(ROW_HEADER_WIDTH_PX, y);
    ctx.stroke();
    ctx.fillStyle = theme.headerText;
    // 1-based row numbers: A1 notation lives at the boundary, and this is it.
    ctx.fillText(String(row + 1), ROW_HEADER_WIDTH_PX / 2, rect.y + rect.h / 2);
  }

  ctx.strokeStyle = theme.headerBorder;
  ctx.lineWidth = 1;
  ctx.strokeRect(0, 0, ROW_HEADER_WIDTH_PX, COL_HEADER_HEIGHT_PX);
}

/** Gridlines across the visible region. */
function paintGridlines(ctx: GridContext2D, model: PaintModel, theme: GridTheme): void {
  const v = normalizeRange(model.visible);
  ctx.strokeStyle = theme.gridline;
  ctx.lineWidth = 1;
  ctx.beginPath();
  // One path for every line in the frame: a `stroke()` per line is what makes a
  // naive canvas grid miss the frame budget.
  for (let col = v.c0; col <= v.c1; col++) {
    const rect = cellRect(model, v.r0, col);
    const x = Math.floor(rect.x) + 0.5;
    ctx.moveTo(x, COL_HEADER_HEIGHT_PX);
    ctx.lineTo(x, model.heightPx);
    if (col === v.c1) {
      const xEnd = Math.floor(rect.x + rect.w) + 0.5;
      ctx.moveTo(xEnd, COL_HEADER_HEIGHT_PX);
      ctx.lineTo(xEnd, model.heightPx);
    }
  }
  for (
    let row = nextVisible(model.rowAxis, v.r0);
    row <= v.r1;
    row = nextVisible(model.rowAxis, row + 1)
  ) {
    const rect = cellRect(model, row, v.c0);
    const y = Math.floor(rect.y) + 0.5;
    ctx.moveTo(ROW_HEADER_WIDTH_PX, y);
    ctx.lineTo(model.widthPx, y);
    if (nextVisible(model.rowAxis, row + 1) > v.r1) {
      const yEnd = Math.floor(rect.y + rect.h) + 0.5;
      ctx.moveTo(ROW_HEADER_WIDTH_PX, yEnd);
      ctx.lineTo(model.widthPx, yEnd);
    }
  }
  ctx.stroke();
}

/** The selection wash, its outline, and the active-cell outline. */
function paintSelection(ctx: GridContext2D, model: PaintModel, theme: GridTheme): void {
  const s = normalizeRange(model.selection);
  const head = cellRect(model, s.r0, s.c0);
  const tail = cellRect(model, s.r1, s.c1);
  const rect = {
    x: head.x,
    y: head.y,
    w: tail.x + tail.w - head.x,
    h: tail.y + tail.h - head.y,
  };
  const single = s.r0 === s.r1 && s.c0 === s.c1;
  const anchorCell = model.anchor ?? model.active;
  const activeRect = cellRect(model, anchorCell.row, anchorCell.col);

  if (!single) {
    ctx.fillStyle = theme.selectionFill;
    ctx.fillRect(rect.x, rect.y, rect.w, rect.h);

    // Unshade the anchor cell inside range selection (Row Zero exact behavior)
    ctx.fillStyle = theme.background;
    ctx.fillRect(activeRect.x, activeRect.y, activeRect.w, activeRect.h);

    // Re-paint cell text for active cell so text like A1/D1 is clearly visible
    const cell = model.cache.cell(model.sheet, anchorCell.row, anchorCell.col);
    if (cell !== null) {
      paintCellText(ctx, activeRect, cell, styleOf(cell.tile, cell.styleRef), theme);
    }
  }

  ctx.strokeStyle = theme.selectionBorder;
  ctx.lineWidth = 1.5;
  ctx.strokeRect(Math.floor(rect.x) + 0.5, Math.floor(rect.y) + 0.5, Math.floor(rect.w), Math.floor(rect.h));

  ctx.strokeStyle = theme.activeCellBorder;
  ctx.lineWidth = 2;
  ctx.strokeRect(Math.floor(activeRect.x), Math.floor(activeRect.y), Math.floor(activeRect.w), Math.floor(activeRect.h));

  // The fill handle (Requirement 12.9). Its rectangle comes from `interaction.ts`, the
  // same function the pointer handler grabs it with, so the drawn square and the
  // grabbable square cannot drift apart.
  const handle = fillHandleRect(
    { rowAxis: model.rowAxis, colAxis: model.colAxis, scrollX: model.scrollX, scrollY: model.scrollY, freeze: model.freeze },
    s,
  );
  ctx.fillStyle = theme.fillHandle;
  ctx.fillRect(handle.x, handle.y, handle.w, handle.h);
}

/**
 * Outlines the ranges a formula being edited refers to (Requirement 2.9).
 *
 * Drawn after the selection so a referenced range that overlaps the selection stays
 * visible, and clipped to nothing: a reference far outside the viewport simply
 * produces a rectangle off-canvas, which the context discards.
 */
function paintReferenceHighlights(
  ctx: GridContext2D,
  model: PaintModel,
  theme: GridTheme,
): void {
  const highlights = model.highlights ?? [];
  if (highlights.length === 0) return;
  for (const raw of highlights) {
    const r = normalizeRange(raw);
    if (r.sheet !== model.sheet) continue;
    const head = cellRect(model, r.r0, r.c0);
    const tail = cellRect(model, r.r1, r.c1);
    const rect = {
      x: head.x,
      y: head.y,
      w: tail.x + tail.w - head.x,
      h: tail.y + tail.h - head.y,
    };
    ctx.fillStyle = theme.referenceFill;
    ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
    ctx.strokeStyle = theme.referenceBorder;
    ctx.lineWidth = 2;
    ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
  }
}

/** The two lines that mark a frozen pane (Requirement 1.8). */
function paintFreezeBands(ctx: GridContext2D, model: PaintModel, theme: GridTheme): void {
  const frozen = frozenExtentOf(model);
  if (frozen.rows === 0 && frozen.cols === 0) return;
  ctx.strokeStyle = theme.freezeLine;
  ctx.lineWidth = 2;
  ctx.beginPath();
  if (frozen.rows > 0) {
    const y = COL_HEADER_HEIGHT_PX + frozen.heightPx;
    ctx.moveTo(0, y);
    ctx.lineTo(model.widthPx, y);
  }
  if (frozen.cols > 0) {
    const x = ROW_HEADER_WIDTH_PX + frozen.widthPx;
    ctx.moveTo(x, 0);
    ctx.lineTo(x, model.heightPx);
  }
  ctx.stroke();
}

/**
 * Paints one frame.
 *
 * The visible range comes from the model rather than being recomputed here, so the
 * grid view, the hit tester, and the painter all agree on one answer per frame.
 */
export function paintGrid(ctx: GridContext2D, model: PaintModel): PaintStats {
  const started = now();
  const theme = model.theme ?? DEFAULT_THEME;
  const v = normalizeRange(model.visible);
  const stats: PaintStats = { cells: 0, missing: 0, merged: 0, overflow: 0, elapsedMs: 0 };

  ctx.fillStyle = theme.background;
  ctx.fillRect(0, 0, model.widthPx, model.heightPx);

  paintGridlines(ctx, model, theme);

  // Merged regions are painted once, from their head cell, and their covered cells
  // are skipped: a merge is one displayed cell (Requirement 6.8).
  const paintedMerges = new Set<Range>();

  // Hidden rows are stepped over rather than drawn at zero height: a filter can hide a
  // million rows between two visible ones, and a frame may not be proportional to the
  // gap (Requirements 1.5, 7.4).
  for (
    let row = nextVisible(model.rowAxis, v.r0);
    row <= v.r1;
    row = nextVisible(model.rowAxis, row + 1)
  ) {
    for (let col = v.c0; col <= v.c1; col++) {
      const region = mergeCovering(model.merges, row, col);
      if (region !== null) {
        if (paintedMerges.has(region)) continue;
        paintedMerges.add(region);
        const cell = model.cache.cell(model.sheet, region.r0, region.c0);
        const rect = mergeRect(model, region);
        stats.merged += 1;
        if (cell === null) {
          stats.missing += 1;
          continue;
        }
        paintCellBox(ctx, rect, styleOf(cell.tile, cell.styleRef));
        paintCellText(ctx, rect, cell, styleOf(cell.tile, cell.styleRef), theme);
        if ((cell.flags & FLAG_OVERFLOW) !== 0) stats.overflow += 1;
        continue;
      }

      stats.cells += 1;
      const cell = model.cache.cell(model.sheet, row, col);
      if (cell === null) {
        stats.missing += 1;
        continue;
      }
      const style = styleOf(cell.tile, cell.styleRef);
      const rect = cellRect(model, row, col);

      paintCellBox(ctx, rect, style);
      paintCellText(ctx, rect, cell, style, theme);
      if ((cell.flags & FLAG_OVERFLOW) !== 0) stats.overflow += 1;
    }
  }

  paintSelection(ctx, model, theme);
  paintReferenceHighlights(ctx, model, theme);
  paintFreezeBands(ctx, model, theme);
  paintHeaders(ctx, model, theme);

  stats.elapsedMs = now() - started;
  return stats;
}
